import { generateId } from './id';
import { nodeIdOr, unscopedNodeId } from './stableNodeId';
import { Maybe } from './maybeFunctor';
import * as BufferGeometryUtils from "three/examples/jsm/utils/BufferGeometryUtils";
import { Mesh, MeshBasicMaterial, MeshPhongMaterial, BufferGeometry, Box3, Matrix4, Vector3,
   DoubleSide, InstancedMesh, Box3Helper, DynamicDrawUsage, Float32BufferAttribute
   } from 'three'

// AN ABSENT COLLECTION AND AN EMPTY ONE MEAN THE SAME THING HERE.
//
// The timeline viewer threw "Cannot read properties of undefined (reading
// 'forEach')" on every load and the MODEL NEVER APPEARED. Traced through the
// deployed source map: helpers/three.js:218, called from
// TimelineViewer.jsx:657 - generateSubprojectMeshes(res.UnmatchedSubProjects).
// The response simply had no UnmatchedSubProjects, which is normal for an
// estimate with nothing unmatched, and the throw aborted the WHOLE onLoad, so
// every line after it never ran.
//
// The guard already existed in this file, applied ONCE, to the single field
// that happened to break the day somebody hit it: (json.DisabledSubprojects
// || []). Its neighbours were left unguarded, and so was every helper that
// takes a list as an ARGUMENT. Ten reads across this file could throw the
// same way; fixing only the one in the stack trace would have left nine.
//
// These buckets - Unmatched, Existing, Demolition, Disabled - are variants
// that are frequently absent. Absent is not an error, it is zero of them, so
// [] is the ACCURATE answer and not a fallback hiding something. A malformed
// payload that genuinely lacks the MAIN SubProjects still renders an empty
// scene rather than a blank page, which is what it always did once this threw.
const asList = (v) => (Array.isArray(v) ? v : []);

export const generateRawMeshes = json => {
  const meshes = [];
  asList(json.SubProjects).forEach(SubProject => {
    asList(SubProject.Groups).forEach(group => {
      asList(group.Components).forEach(component => {
        meshes.push(component);
      });
    });
  });

  asList(json.ExistingSubProjects).forEach(SubProject => {
    asList(SubProject.Groups).forEach(group => {
      asList(group.Components).forEach(component => {
        meshes.push(component);
      });
    });
  });
  asList(json.DemolitionSubprojects).forEach(SubProject => {
    asList(SubProject.Groups).forEach(group => {
      asList(group.Components).forEach(component => {
        meshes.push(component);
      });
    });
  });
  // Disabled subprojects — same iteration shape as the others; pulled
  // into the same flat meshes[] so downstream `find(mesh => mesh.UniqueId === id)`
  // finds disabled components without any extra branching.
  asList(json.DisabledSubprojects).forEach(SubProject => {
    asList(SubProject.Groups).forEach(group => {
      asList(group.Components).forEach(component => {
        meshes.push(component);
      });
    });
  });
  return meshes;
};

export const generateMeshInfo = (json) => {
  const meshes = [];
  const arr = json?.map((item) => (
    item.forEach((i) => {
      i.nodes.forEach((j) => {
        j.nodes.forEach((k) => {
          k.nodes.forEach((l) => {
            meshes.push(l.meshInfo);
          })
        });
      });
    })
  ))

  return meshes;
};

export const generateRawTree = json => {
  let fullSet = [...asList(json.SubProjects).map(subproject => {
    // A SubProject has no id of its own; `$id` is the serializer's reference
    // number and can be absent. Deterministic fallback, assigned here at
    // BUILD time - never at render, which is what key={generateId()} did.
    const subId = nodeIdOr(subproject.$id, 'sub', json.$id, subproject.Category);
    return {
      id: subId,
      parentId: json.$id,
      treeVisible: true,
      key: subId,
      label: subproject.Category,
      nodes: asList(subproject.Groups).map(group => {
        const groupId = nodeIdOr(group.$id, 'group', json.$id, subproject.Category, group.Name);
        return {
          id: groupId,
          parentId: subId,
          treeVisible: true,
          key: groupId,
          label: group.Name,
          nodes: asList(group.Components).map(component => {
            return {
              id: component.$id,
              parentId: group.$id,
              key: component.$id,
              treeVisible: true,
              label: component.Name,
              meshInfo: component,
              nodes: [],
            };
          }),
        };
      }),
    };
  }), ...asList(json.ExistingSubProjects).map(subproject => {
    return {
      id: nodeIdOr(subproject.$id, 'sub', json.$id, subproject.Category),
      parentId: json.$id,
      treeVisible: true,
      key: nodeIdOr(subproject.$id, 'sub', json.$id, subproject.Category),
      label: ' (Existing) ' + subproject.Category,
      nodes: asList(subproject.Groups).map(group => {
        return {
          id: nodeIdOr(group.$id, 'group', json.$id, subproject.Category, group.Name),
          parentId: nodeIdOr(subproject.$id, 'sub', json.$id, subproject.Category),
          treeVisible: true,
          key: nodeIdOr(group.$id, 'group', json.$id, subproject.Category, group.Name),
          label: ' (Existing) ' + group.Name,
          nodes: asList(group.Components).map(component => {
            return {
              id: component.$id,
              parentId: group.$id,
              key: component.$id,
              treeVisible: true,
              label: ' (Existing) ' + component.Name,
              meshInfo: component,
              nodes: [],
            };
          }),
        };
      }),
    };
  }), ...asList(json.DemolitionSubprojects).map(subproject => {
    return {
      id: nodeIdOr(subproject.$id, 'sub', json.$id, subproject.Category),
      parentId: json.$id,
      treeVisible: true,
      key: nodeIdOr(subproject.$id, 'sub', json.$id, subproject.Category),
      label: ' (Demolition) ' + subproject.Category,
      nodes: asList(subproject.Groups).map(group => {
        return {
          id: nodeIdOr(group.$id, 'group', json.$id, subproject.Category, group.Name),
          parentId: nodeIdOr(subproject.$id, 'sub', json.$id, subproject.Category),
          treeVisible: true,
          key: nodeIdOr(group.$id, 'group', json.$id, subproject.Category, group.Name),
          label: ' (Demolition) ' + group.Name,
          nodes: asList(group.Components).map(component => {
            return {
              id: component.$id,
              parentId: group.$id,
              key: component.$id,
              treeVisible: true,
              label: ' (Demolition) ' + component.Name,
              meshInfo: component,
              nodes: [],
            };
          }),
        };
      }),
    };
  }), ...((json.DisabledSubprojects || []).map(subproject => {
    // Disabled subprojects render in the unified raw tree the same way
    // existing/demolition do — prefixed labels keep them visually grouped
    // when the tree is rendered without per-section headers.
    return {
      id: nodeIdOr(subproject.$id, 'sub', json.$id, subproject.Category),
      parentId: json.$id,
      treeVisible: true,
      key: nodeIdOr(subproject.$id, 'sub', json.$id, subproject.Category),
      label: ' (Disabled) ' + subproject.Category,
      nodes: asList(subproject.Groups).map(group => {
        return {
          id: nodeIdOr(group.$id, 'group', json.$id, subproject.Category, group.Name),
          parentId: nodeIdOr(subproject.$id, 'sub', json.$id, subproject.Category),
          treeVisible: true,
          key: nodeIdOr(group.$id, 'group', json.$id, subproject.Category, group.Name),
          label: ' (Disabled) ' + group.Name,
          nodes: asList(group.Components).map(component => {
            return {
              id: component.$id,
              parentId: group.$id,
              key: component.$id,
              treeVisible: true,
              label: ' (Disabled) ' + component.Name,
              meshInfo: component,
              nodes: [],
            };
          }),
        };
      }),
    };
  }))]
  console.log("fullSet structure ", fullSet)
  return [
    {
      key: json.$id,
      label: json.Title,
      id: json.$id,
      parentId: json.$id,
      treeVisible: true,
      nodes: fullSet
    }
  ];
};

export const generateSubprojectMeshesHashed = subproejcts => {
  const meshes = new Map();
  asList(subproejcts).forEach(SubProject => {
    asList(SubProject.Groups).forEach(group => {
      asList(group.Components).forEach(component => {
        meshes.set(component.UniqueId, component);
      });
    });
  });
  return meshes;
};

export const generateMeshes = json => {
  const meshes = [];
  asList(json.SubProjects).forEach(SubProject => {
    asList(SubProject.Groups).forEach(group => {
      asList(group.Components).forEach(component => {
        meshes.push(component);
      });
    });
  });
  return meshes;
};

export const generateSubprojectMeshes = subproejcts => {
  const meshes = [];
  asList(subproejcts).forEach(SubProject => {
    asList(SubProject.Groups).forEach(group => {
      asList(group.Components).forEach(component => {
        meshes.push(component);
      });
    });
  });
  return meshes;
};

export const generateSubprojectHashmap = ($id, SubProjects) => {
  const hashmap = new Map();

  asList(SubProjects).forEach(subproject => {
    hashmap.set(subproject.$id, {
      id: subproject.$id,
      parentId: $id,
      treeId: $id,
      treeVisible: true,
      treeSelected: false,
      key: subproject.$id,
      label: subproject.Category,
      nodes: subproject.Groups
    })

    asList(subproject.Groups).forEach(group => {
      hashmap.set(group.$id,
        {
          id: group.$id,
          parentId: subproject.$id,
          treeId: $id,
          treeVisible: true,
          treeSelected: false,
          key: group.$id,
          label: group.Name,
          nodes: group.Components
        })

      asList(group.Components).forEach(component => {
        hashmap.set(component.$id, {
          id: component.$id,
          parentId: group.$id,
          treeId: $id,
          key: component.$id,
          treeVisible: true,
          treeSelected: false,
          label: component.Name,
          meshInfo: component,
          nodes: [],
        })
      });
    });
  });

  return hashmap;
};

export const generateSubprojectTree = ($id, Title, SubProjects) => {
  return [
    {
      key: $id,
      label: Title,
      id: $id,
      parentId: $id,
      treeVisible: true,
      treeSelected: false,
      nodes: asList(SubProjects).map(subproject => {
        return {
          id: subproject.$id,
          parentId: $id,
          treeVisible: true,
          treeSelected: false,
          key: subproject.$id,
          label: subproject.Category,
          nodes: asList(subproject.Groups).map(group => {
            return {
              id: group.$id,
              parentId: subproject.$id,
              treeVisible: true,
              treeSelected: false,
              key: group.$id,
              label: group.Name,
              nodes: asList(group.Components).map(component => {
                return {
                  id: component.$id,
                  parentId: group.$id,
                  key: component.$id,
                  treeVisible: true,
                  treeSelected: false,
                  label: component.Name,
                  meshInfo: component,
                  nodes: [],
                };
              }),
            };
          }),
        };
      }),
    },
  ];
};

export const generateTree = json => {
  const mainParentId = generateId();
  return [
    {
      key: json.$id,
      label: json.Title,
      id: mainParentId,
      parentId: mainParentId,
      treeVisible: true,
      jsonId: json.$id,
      nodes: asList(json.SubProjects).map(subproject => {
        const mediumParentId = generateId();
        return {
          id: mediumParentId,
          parentId: mainParentId,
          treeVisible: true,
          jsonId: subproject.$id,
          key: json.$id,
          label: subproject.Category,
          nodes: asList(subproject.Groups).map(group => {
            const deepParentId = generateId();
            return {
              id: deepParentId,
              parentId: mediumParentId,
              jsonId: group.$id,
              treeVisible: true,
              key: json.$id,
              label: group.Name,
              nodes: asList(group.Components).map(component => {
                return {
                  id: generateId(),
                  parentId: deepParentId,
                  key: json.$id,
                  treeVisible: true,
                  label: component.Name,
                  meshInfo: component,
                  jsonId: component.$id,
                  nodes: [],
                };
              }),
            };
          }),
        };
      }),
    },
  ];
};


export const findRecursiveInTreeList = (isComposed, treeList, id) => {
  let result = null;
  let parent = null;
  let resultPath = [];

  treeList.map((treefile) => {
    return Maybe.of(treefile).map(tree => (
      (function search(tree, id, par, path = [tree]) {
        if (tree.id === id) {
          result = tree;
          parent = par;
          resultPath = path;
        } else if (!result) {
          tree.nodes.forEach(item => {
            const newPath = [...path, item];
            search(item, id, tree, newPath);
          });
        }
      })(tree[0], id)
    ))
  })

  return {
    result: result,
    parent: parent,
    path: resultPath,
  };
};

export const findRecursiveInTree = (tree, id) => {
  let result = null;
  let parent = null;
  let resultPath = [];

  (function search(tree, id, par, path = [tree]) {
    if (tree.key === id) {
      result = tree;
      parent = par;
      resultPath = path;
    } else if (!result) {
      tree.nodes.forEach(item => {
        const newPath = [...path, item];
        search(item, id, tree, newPath);
      });
    }
  })(tree[0], id);

  return {
    result: result,
    parent: parent,
    path: resultPath,
  };
};

export const flattenTree = (tree, flattenedTree = []) => {
  if (tree.nodes.length === 0) {
    flattenedTree.push(tree);
  } else if (tree.nodes.length > 0) {
    tree.nodes.forEach(n => {
      if (n) {
        flattenTree(n, flattenedTree);
      }
    });
  }
  return flattenedTree;
};

// im - InstancedMesh
// instanceIdx - index of an instance
// visible - boolean show or hide
export const instanceShow = (im, instanceIdx, visible) => {
  if (!im || !im.userData) return;
  const ud = im.userData[instanceIdx];
  if (!ud || !ud.transformation) return;

  const transformation = ud.transformation;
  const matrix = new Matrix4();

  if (visible) {
    matrix.set(
      transformation.m0, transformation.m1, transformation.m2, transformation.m3,
      transformation.m4, transformation.m5, transformation.m6, transformation.m7,
      transformation.m8, transformation.m9, transformation.m10, transformation.m11,
      transformation.m12, transformation.m13, transformation.m14, transformation.m15);
  } else {
    matrix.set(
      transformation.m0, transformation.m1, transformation.m2, transformation.m3,
      transformation.m4, transformation.m5, transformation.m6, transformation.m7,
      transformation.m8, transformation.m9, transformation.m10, transformation.m11,
      transformation.m12, transformation.m13, transformation.m14, 0);
  }

  im.setMatrixAt(instanceIdx, matrix);
  im.instanceMatrix.needsUpdate = true;
}

// Show or hide an element. Handles three shapes:
//   1) multi-fragment instanced: meshObj.fragments = [{ mesh, index }, ...]
//   2) legacy single instanced: meshObj.mesh + meshObj.index
//   3) regular meshes / nested children
export const UpdateGroupVisibility = (meshObj, visibility) => {
  if (!meshObj) return;

  if (Array.isArray(meshObj.fragments) && meshObj.fragments.length > 0) {
    meshObj.fragments.forEach(({ mesh, index }) => {
      if (!mesh) return;
      if (mesh.isInstancedMesh) {
        instanceShow(mesh, index, visibility);
      } else {
        mesh.visible = visibility;
      }
    });
    return;
  }

  if (meshObj.mesh?.isMesh) {
    if (meshObj.mesh.isInstancedMesh) {
      instanceShow(meshObj.mesh, meshObj.index, visibility);
    } else {
      meshObj.mesh.visible = visibility;
    }
  } else if (meshObj.mesh?.children) {
    meshObj.mesh.children.forEach(m => {
      if (m) {
        UpdateGroupVisibility(m, visibility);
      }
    });
  }
};

export const GetChildrenMeshes = (meshObj, oldMeshes = []) => {
  if (meshObj.mesh.isMesh) {
    oldMeshes.push(meshObj);
  } else if (meshObj.mesh.children) {
    meshObj.mesh.children.forEach(m => {
      if (m) {
        GetChildrenMeshes(m, oldMeshes);
      }
    });
  }
  return oldMeshes;
};

export const MergeChildren = (meshObj, box3 = new Box3()) => {
  if (meshObj.mesh.isMesh) {
    const mesh = this.meshesObject.get(meshObj.uniqueId);
    box3.expandByObject(mesh);
  } else if (meshObj.mesh.children) {
    meshObj.mesh.children.forEach(m => {
      if (m) {
        MergeChildren(m, box3);
      }
    });
  }

  const dimensions = new Vector3().subVectors(box3.max, box3.min);
  const boxGeo = new BufferGeometry(dimensions.x, dimensions.y, dimensions.z);
  const matrix = new Matrix4().setPosition(dimensions.addVectors(box3.min, box3.max).multiplyScalar(0.5));

  return new Mesh(boxGeo.applyMatrix4(matrix), new MeshBasicMaterial({ color: 0xffcc55 }));
};

export const SetChildrenDepthWrite = (mesh) => {
  if (mesh.isMesh) {
    mesh.material.colorWrite = true;

    if (mesh.material.opacity < 1) {
      mesh.material.transparent = true;
    } else {
      mesh.material.transparent = false;
    }

    mesh.material.depthTest = true;
    mesh.material.depthWrite = true;
  } else if (mesh.children) {
    mesh.children.forEach(m => {
      if (m) {
        SetChildrenDepthWrite(m);
      }
    });
  }
};

export const GetChildrenMaterials = (meshObj, oldMaterials = []) => {
  if (meshObj.mesh.isMesh) {
    oldMaterials.push(meshObj.mesh.material);
  } else if (meshObj.mesh.children) {
    meshObj.mesh.children.forEach(m => {
      if (m) {
        GetChildrenMaterials(m, oldMaterials);
      }
    });
  }
  return oldMaterials;
};

export const GetChildrenids = (meshObj, ids = []) => {
  if (meshObj.mesh.isMesh) {
    ids.push(meshObj.UniqueId);
  } else if (meshObj.mesh.children) {
    meshObj.mesh.children.forEach(m => {
      if (m) {
        GetChildrenids(m, ids);
      }
    });
  }
  return ids;
};

export const SetChildrenMaterial = (meshObj, material, oldMaterials = []) => {
  if (meshObj.mesh.isMesh) {
    oldMaterials.push(meshObj.mesh.material);

    if (typeof meshObj.mesh.setColorAt === 'function') {
      meshObj.mesh.setColorAt(meshObj.index, material.color);
      if (meshObj.mesh.instanceColor) {
        meshObj.mesh.instanceColor.needsUpdate = true;
      }
    }
  } else if (meshObj.mesh.children) {
    meshObj.mesh.children.forEach(m => {
      if (m) {
        SetChildrenMaterial(m, material, oldMaterials);
      }
    });
  }
  return oldMaterials;
};

// Restore the original color/material for a flat list of fragment-meshes
// belonging to one element. The `i` cursor walks meshObjList; materialList
// is passed unchanged through the recursion (the prior version mistakenly
// passed `materialList[i]` and broke after the first item).
export const SetChildrenMaterialsBack = (meshObjList, materialList, i = 0) => {
  if (meshObjList && meshObjList[i] !== undefined) {
    if (meshObjList[i].mesh.isMesh) {
      const m = meshObjList[i].mesh;
      const original = materialList[i];

      if (original && typeof m.setColorAt === 'function' && original.color) {
        m.setColorAt(meshObjList[i].index, original.color);
        m.needsUpdate = true;
        if (m.instanceColor) {
          m.instanceColor.needsUpdate = true;
        }
      } else if (original && m.isMesh && !m.isInstancedMesh) {
        m.material = original;
        m.material.needsUpdate = true;
      }

      i++;
      SetChildrenMaterialsBack(meshObjList, materialList, i);
    }
  } else if (meshObjList && meshObjList.children) {
    meshObjList.mesh.children.forEach(m => {
      if (m) {
        SetChildrenMaterialsBack(m, materialList, i);
      }
    });
  }
};

// FIND A NODE BY WHAT IT IS, NOT BY ONE FIELD OF IT.
//
// 2026-10-21, from the owner's own click trace: findRecord hit, the lazy row
// was found, the leaf was inserted - and then `pick: path` came back with
// depth 0, so nothing opened, nothing was highlighted and there was no key to
// scroll to. The walk below searches for `node.id === $id`, and $id is a
// JSON.NET reference number. Since nodeIdOr, a node whose export carries no
// $id is built with a DERIVED id instead, and the search - still asking for
// $id, which is undefined - matches nothing. (Before nodeIdOr it matched the
// first node whose id was also undefined, which is where "it highlights the
// wrong item" came from. Both symptoms, one cause.)
//
// A component always has Revit's UniqueId, so the caller can ask by whatever
// it actually holds. Two other faults fixed in the same walk:
//   - it searched tree[0] ONLY, so an element under any root but the first
//     was unreachable;
//   - `tree.nodes.forEach` threw on a leaf with no `nodes` array.
export const findRecursiveInTreeMatch = (tree, match) => {
  let result = null;
  let parent = null;
  let resultPath = [];
  // One level of array is a list of document trees, each of which is itself a
  // single-root array. Flatten whatever we were handed into actual nodes.
  const roots = [];
  const push = (t) => {
    if (!t) return;
    if (Array.isArray(t)) { t.forEach(push); return; }
    roots.push(t);
  };
  push(tree);

  const search = (node, par, path) => {
    if (result || !node) return;
    if (match(node)) {
      result = node;
      parent = par;
      resultPath = path;
      return;
    }
    const kids = Array.isArray(node.nodes) ? node.nodes : [];
    for (const item of kids) {
      search(item, node, [...path, item]);
      if (result) return;
    }
  };
  for (const root of roots) {
    search(root, null, [root]);
    if (result) break;
  }
  return { result, parent, path: resultPath };
};

/**
 * WHICH RECORD A CLICK MEANT.
 *
 * The viewer hands a pick to the page in two shapes, and which one you get
 * depends on the page: ModelViewer and Viewer3d pass ONE ENTRY PER DOCUMENT
 * (findClickedMesh's `perFile`, undefined where that document does not hold
 * the element), while TimelineViewer and SocketViewer pass the single record
 * their own lookup found. So `clicked.forEach(...)` throws on one shape and
 * `clicked.length > 1` silently yields nothing on the other - which is
 * exactly what the staging drive of 2026-10-21 showed on the timeline: the
 * element was found in 3D (meshFound true) and the React half then expanded,
 * highlighted and scrolled nothing, because the record it searched for was
 * undefined.
 *
 * One rule, said once: a list means "the last document that holds it", a bare
 * record means itself, and nothing means null rather than a throw.
 */
export const recordFromClick = (clicked) => {
  if (!clicked) return null;
  if (!Array.isArray(clicked)) return clicked;
  let last = null;
  clicked.forEach((m) => { if (m) last = m; });
  return last;
};

/**
 * THE ROW FOR A CLICKED RECORD - the one call every viewer page should make.
 *
 * Owner, 2026-10-21: "pull the code from all other viewers and fix same
 * issue". Six pages each searched the tree for `node.id === record.$id`, and
 * all six have the bug the trace caught on the model viewer: a node built
 * without $id carries a derived id, so the search matches nothing and the
 * click opens, highlights and scrolls nothing.
 *
 * Identities are tried in order of how much they are worth:
 *   1. the id of the leaf a lazy path just inserted - exact, we built it;
 *   2. the element's Revit UniqueId - a component always has one;
 *   3. $id - for the eager trees that have always worked this way.
 */
export const findRowForRecord = (treeMeshes, record, preferredNodeId) => {
  const uid = record && record.UniqueId;
  const jsonId = record && record.$id;
  const hasJson = jsonId !== undefined && jsonId !== null && jsonId !== '';
  // ONE PASS PER IDENTITY, NOT ONE PASS FOR ALL THREE. $id restarts at "1" in
  // every file, so a single walk that took the first node matching ANY of them
  // found the main file's element "4" before it reached the linked file's
  // element by its UniqueId - a 3D click on a linked element opened the main
  // file's tree (owner, 2026-10-23: "it's confusing 2 files trees together").
  // The UniqueId names one element in one file, so it is asked of every tree
  // first; the ids only when no tree has it.
  const leafRow = (n) => !!n.meshInfo;
  let found = { result: null };
  if (uid) found = findRecursiveInTreeMatch(treeMeshes, (n) => leafRow(n) && n.meshInfo.UniqueId === uid);
  if (!found.result && preferredNodeId) found = findRecursiveInTreeMatch(treeMeshes, (n) => n.id === preferredNodeId);
  if (!found.result && hasJson) found = findRecursiveInTreeMatch(treeMeshes, (n) => unscopedNodeId(n.id) === String(jsonId));
  let by = 'none';
  if (found.result) {
    if (preferredNodeId && found.result.id === preferredNodeId) by = 'leafId';
    else if (uid && found.result.meshInfo && found.result.meshInfo.UniqueId === uid) by = 'uid';
    else by = 'jsonId';
  }
  return { ...found, by, hadLeafId: !!preferredNodeId, hadJsonId: hasJson };
};

export const findRecursiveInTreeJsonId = (tree, jsonId) => {
  let result = null;
  let parent = null;
  let resultPath = [];

  (function search(tree, jsonId, par, path = [tree]) {
    if (tree.id === jsonId) {
      result = tree;
      parent = par;
      resultPath = path;
    } else if (!result) {
      tree.nodes.forEach(item => {
        const newPath = [...path, item];
        search(item, jsonId, tree, newPath);
      });
    }
  })(tree[0], jsonId);

  return {
    result: result,
    parent: parent,
    path: resultPath,
  };
};

/**
 * THE SAME IDENTITY RULE, FOR THE PAGES THAT HOLD A MAP INSTEAD OF A TREE.
 *
 * SocketViewer keeps its nodes in a Map keyed by node id, so it cannot use
 * findRowForRecord. It has the same bug and one of its own: `newClicked` was
 * only assigned when more than one file matched, so the single-file path
 * looked up map.get(undefined) on EVERY click and could never resolve.
 *
 * Tries the map by the ids we may have, and only on a miss scans the values
 * for the element's UniqueId - O(n) on a path that previously returned
 * nothing at all.
 */
export const findRowInNodeMap = (mapRef, record, preferredNodeId) => {
  const map = mapRef && mapRef.current ? mapRef.current : mapRef;
  if (!map || typeof map.get !== 'function') return { result: null, parent: null, by: 'none' };
  let result = null;
  let by = 'none';
  if (preferredNodeId && map.get(preferredNodeId)) { result = map.get(preferredNodeId); by = 'leafId'; }
  if (!result && record && record.$id !== undefined && record.$id !== null && map.get(record.$id)) {
    result = map.get(record.$id); by = 'jsonId';
  }
  if (!result && record && record.UniqueId && typeof map.values === 'function') {
    for (const node of map.values()) {
      if (node && node.meshInfo && node.meshInfo.UniqueId === record.UniqueId) { result = node; by = 'uid'; break; }
    }
  }
  return { result: result || null, parent: result ? map.get(result.parentId) || null : null, by };
};

export const findRecursiveInTreeJsonIdHashmap = (tree, jsonId) => {
  let result = tree.current.get(jsonId);
  let parent = null;
  if (result) {
    parent = tree.current.get(result.parentId);
  }

  return {
    result: result,
    parent: parent,
  };
};

export const findRecursiveInTreeListJsonId = (treeList, jsonId) => {
  let result = null;
  let parent = null;
  let resultPath = [];
  if (isIterable(treeList)) {
    treeList.map((treefile, index) => {
      return Maybe.of(treefile).map(tree => (
        (function search(tree, jsonId, par, path = [tree]) {
          if (tree.id === jsonId) {
            result = tree;
            parent = par;
            resultPath = path;
          } else if (!result) {
            tree.nodes.forEach(item => {
              const newPath = [...path, item];
              search(item, jsonId, tree, newPath);
            });
          }
        })(tree[0], jsonId)))
    })
  } else {
    (function search(treeList, jsonId, par, path = [treeList]) {
      if (treeList.id === jsonId) {
        result = treeList;
        parent = par;
        resultPath = path;
      } else if (!result) {
        treeList.nodes.forEach(item => {
          const newPath = [...path, item];
          search(item, jsonId, treeList, newPath);
        });
      }
    })(treeList[0], jsonId)
  }

  return {
    result: result,
    parent: parent,
    path: resultPath,
  };
};

export const isIterable = (x) => { return !!x?.[Symbol.iterator] };


let _cb = new Vector3();
let _ab = new Vector3();

export const modify = (geometry, count) => {
  _cb = new Vector3();
  _ab = new Vector3();

  const attributes = geometry.attributes;

  for (const name in attributes) {
    if (name !== 'position') geometry.deleteAttribute(name);
  }

  geometry = BufferGeometryUtils.mergeVertices(geometry);

  const vertices = [];
  const faces = [];

  const positionAttribute = geometry.getAttribute('position');

  for (let i = 0; i < positionAttribute.count; i++) {
    const v = new Vector3().fromBufferAttribute(positionAttribute, i);
    const vertex = new Vertex(v);
    vertices.push(vertex);
  }

  let index = geometry.getIndex();

  if (index !== null) {
    for (let i = 0; i < index.count; i += 3) {
      const a = index.getX(i);
      const b = index.getX(i + 1);
      const c = index.getX(i + 2);
      const triangle = new Triangle(vertices[a], vertices[b], vertices[c], a, b, c);
      faces.push(triangle);
    }
  } else {
    for (let i = 0; i < positionAttribute.count; i += 3) {
      const a = i;
      const b = i + 1;
      const c = i + 2;
      const triangle = new Triangle(vertices[a], vertices[b], vertices[c], a, b, c);
      faces.push(triangle);
    }
  }

  for (let i = 0, il = vertices.length; i < il; i++) {
    computeEdgeCostAtVertex(vertices[i]);
  }

  const arrIncreasingCostVertices = Array(vertices.length).fill().map((_, index) => { return index })

  arrIncreasingCostVertices.sort(function (index1, index2) {
    return vertices[index1].collapseCost - vertices[index2].collapseCost;
  });

  const arrVerticesToRemove = arrIncreasingCostVertices.slice(0, count);

  arrVerticesToRemove.sort((index1, index2) => { return index2 - index1; });

  if (arrVerticesToRemove.length < count) {
    console.log("Only found " + arrVerticesToRemove.length + " vertices to remove.");
  }

  for (const nextVertexIndex of arrVerticesToRemove) {
    const nextVertex = vertices[nextVertexIndex];
    collapse(vertices, faces, nextVertex, nextVertex.collapseNeighbor);
  }

  const simplifiedGeometry = new BufferGeometry();
  const position = [];
  index = [];

  for (let i = 0; i < vertices.length; i++) {
    const vertex = vertices[i].position;
    position.push(vertex.x, vertex.y, vertex.z);
    vertices[i].id = i;
  }

  for (let i = 0; i < faces.length; i++) {
    const face = faces[i];
    index.push(face.v1.id, face.v2.id, face.v3.id);
  }

  simplifiedGeometry.setAttribute('position', new Float32BufferAttribute(position, 3));
  simplifiedGeometry.setIndex(index);
  return simplifiedGeometry;
}

function pushIfUnique(array, object) {
  if (array.indexOf(object) === - 1) array.push(object);
}

function removeFromArray(array, object) {
  const k = array.indexOf(object);
  if (k > - 1) array.splice(k, 1);
}

function computeEdgeCollapseCost(u, v) {
  const edgelength = v.position.distanceTo(u.position);
  let curvature = 0;
  const sideFaces = [];

  for (let i = 0, il = u.faces.length; i < il; i++) {
    const face = u.faces[i];
    if (face.hasVertex(v)) {
      sideFaces.push(face);
    }
  }

  for (let i = 0, il = u.faces.length; i < il; i++) {
    let minCurvature = 1;
    const face = u.faces[i];

    for (let j = 0; j < sideFaces.length; j++) {
      const sideFace = sideFaces[j];
      const dotProd = face.normal.dot(sideFace.normal);
      minCurvature = Math.min(minCurvature, (1.001 - dotProd) / 2);
    }

    curvature = Math.max(curvature, minCurvature);
  }

  const borders = 0;

  if (sideFaces.length < 2) {
    curvature = 1;
  }

  const amt = edgelength * curvature + borders;
  return amt;
}

function computeEdgeCostAtVertex(v) {
  if (v.neighbors.length === 0) {
    v.collapseNeighbor = null;
    v.collapseCost = - 0.01;
    return;
  }

  v.collapseCost = 100000;
  v.collapseNeighbor = null;

  for (let i = 0; i < v.neighbors.length; i++) {
    const collapseCost = computeEdgeCollapseCost(v, v.neighbors[i]);

    if (!v.collapseNeighbor) {
      v.collapseNeighbor = v.neighbors[i];
      v.collapseCost = collapseCost;
      v.minCost = collapseCost;
      v.totalCost = 0;
      v.costCount = 0;
    }

    v.costCount++;
    v.totalCost += collapseCost;

    if (collapseCost < v.minCost) {
      v.collapseNeighbor = v.neighbors[i];
      v.minCost = collapseCost;
    }
  }

  v.collapseCost = v.totalCost / v.costCount;
}

function removeVertex(v, vertices) {
  console.assert(v.faces.length === 0);

  while (v.neighbors.length) {
    const n = v.neighbors.pop();
    removeFromArray(n.neighbors, v);
  }

  removeFromArray(vertices, v);
}

function removeFace(f, faces) {
  removeFromArray(faces, f);
  if (f.v1) removeFromArray(f.v1.faces, f);
  if (f.v2) removeFromArray(f.v2.faces, f);
  if (f.v3) removeFromArray(f.v3.faces, f);

  const vs = [f.v1, f.v2, f.v3];

  for (let i = 0; i < 3; i++) {
    const v1 = vs[i];
    const v2 = vs[(i + 1) % 3];
    if (!v1 || !v2) continue;
    v1.removeIfNonNeighbor(v2);
    v2.removeIfNonNeighbor(v1);
  }
}

function collapse(vertices, faces, u, v) {
  if (!v) {
    removeVertex(u, vertices);
    return;
  }

  const tmpVertices = [];

  for (let i = 0; i < u.neighbors.length; i++) {
    tmpVertices.push(u.neighbors[i]);
  }

  for (let i = u.faces.length - 1; i >= 0; i--) {
    if (u.faces[i] && u.faces[i].hasVertex(v)) {
      removeFace(u.faces[i], faces);
    }
  }

  for (let i = u.faces.length - 1; i >= 0; i--) {
    u.faces[i].replaceVertex(u, v);
  }

  removeVertex(u, vertices);

  for (let i = 0; i < tmpVertices.length; i++) {
    computeEdgeCostAtVertex(tmpVertices[i]);
  }
}

class Triangle {
  constructor(v1, v2, v3, a, b, c) {
    this.a = a;
    this.b = b;
    this.c = c;
    this.v1 = v1;
    this.v2 = v2;
    this.v3 = v3;
    this.normal = new Vector3();
    this.computeNormal();
    v1.faces.push(this);
    v1.addUniqueNeighbor(v2);
    v1.addUniqueNeighbor(v3);
    v2.faces.push(this);
    v2.addUniqueNeighbor(v1);
    v2.addUniqueNeighbor(v3);
    v3.faces.push(this);
    v3.addUniqueNeighbor(v1);
    v3.addUniqueNeighbor(v2);
  }

  computeNormal() {
    const vA = this.v1.position;
    const vB = this.v2.position;
    const vC = this.v3.position;

    _cb.subVectors(vC, vB);
    _ab.subVectors(vA, vB);
    _cb.cross(_ab).normalize();

    this.normal.copy(_cb);
  }

  hasVertex(v) {
    return v === this.v1 || v === this.v2 || v === this.v3;
  }

  replaceVertex(oldv, newv) {
    if (oldv === this.v1) this.v1 = newv; else if (oldv === this.v2) this.v2 = newv; else if (oldv === this.v3) this.v3 = newv;
    removeFromArray(oldv.faces, this);
    newv.faces.push(this);
    oldv.removeIfNonNeighbor(this.v1);
    this.v1.removeIfNonNeighbor(oldv);
    oldv.removeIfNonNeighbor(this.v2);
    this.v2.removeIfNonNeighbor(oldv);
    oldv.removeIfNonNeighbor(this.v3);
    this.v3.removeIfNonNeighbor(oldv);
    this.v1.addUniqueNeighbor(this.v2);
    this.v1.addUniqueNeighbor(this.v3);
    this.v2.addUniqueNeighbor(this.v1);
    this.v2.addUniqueNeighbor(this.v3);
    this.v3.addUniqueNeighbor(this.v1);
    this.v3.addUniqueNeighbor(this.v2);
    this.computeNormal();
  }
}

class Vertex {
  constructor(v) {
    this.position = v;
    this.id = - 1;
    this.faces = [];
    this.neighbors = [];
    this.collapseCost = 0;
    this.collapseNeighbor = null;
  }

  addUniqueNeighbor(vertex) {
    pushIfUnique(this.neighbors, vertex);
  }

  removeIfNonNeighbor(n) {
    const neighbors = this.neighbors;
    const faces = this.faces;
    const offset = neighbors.indexOf(n);
    if (offset === - 1) return;

    for (let i = 0; i < faces.length; i++) {
      if (faces[i].hasVertex(n)) return;
    }

    neighbors.splice(offset, 1);
  }
}